/**
 * Known UserPreference keys + their canonical client-side types. The
 * backend stores values as strings; the FE hook marshals known keys into
 * their typed values via `serialize` / `deserialize` below.
 *
 * Keys not in this map fall through as opaque strings.
 */
export type Theme = 'light' | 'dark' | 'system';
export type Density = 'comfortable' | 'compact';
/**
 * W16-F5-S1 — landing-preference options.
 *
 * Single source of truth: imported by `SettingsPage.tsx` for the dropdown
 * + by the post-login redirect resolver (W16-F5-S2). Adding a new option
 * here is the only place that needs touching.
 */
export const LANDING_OPTIONS = [
  '/dashboard',
  '/buildings',
  '/buildings/map',
  '/portfolios',
  '/reports',
  '/dashboards/executive',
] as const;

export type DefaultLanding = (typeof LANDING_OPTIONS)[number];

export function isValidLanding(value: unknown): value is DefaultLanding {
  return (
    typeof value === 'string' &&
    (LANDING_OPTIONS as readonly string[]).includes(value)
  );
}

export interface KnownPreferences {
  theme: Theme;
  density: Density;
  defaultLanding: DefaultLanding;
  timezone: string;
  language: string;
  /**
   * Handbook PAGES.md 2.0.0 § 4.7 — language used for outgoing email + digest
   * notifications. Decoupled from UI language so a user reading the app in
   * English can still receive a Norwegian weekly digest. Defaults to the UI
   * `language` value at first read; persisted independently afterwards.
   */
  emailLanguage: string;
  'onboarding.completed': boolean;
  /** W22-F3-S2 — ISO 4217 currency code, e.g. `EUR`, `USD`, `NOK`. */
  currency: string;
  /** W22-F3-S2 — sample number rendering, e.g. `1,234.56`, `1 234,56`. */
  numberFormat: string;
  /** W22-F3-S2 — date pattern string, e.g. `YYYY-MM-DD`, `DD.MM.YYYY`. */
  dateFormat: string;
  /** W22-F3-S3 — Core media id storing the avatar image. `null` = use initials. */
  avatarMediaId: string | null;
}

/** Default values surfaced by `useUserPreference` when no row exists yet. */
export const PREFERENCE_DEFAULTS = {
  currency: 'EUR',
  numberFormat: '1,234.56',
  dateFormat: 'YYYY-MM-DD',
  avatarMediaId: null,
} as const;

export type PreferenceKey = keyof KnownPreferences | (string & {});

export interface UserPreferenceRow {
  id: string;
  userId: string;
  organizationId: string;
  key: string;
  value: string;
  updatedAt: string;
}

const BOOLEAN_KEYS: ReadonlyArray<keyof KnownPreferences> = [
  'onboarding.completed',
];

/** Keys that are allowed to round-trip through `null` — empty string on the
 *  wire, `null` on the JS side. */
const NULLABLE_KEYS: ReadonlyArray<keyof KnownPreferences> = ['avatarMediaId'];

export function deserialize<K extends PreferenceKey>(
  key: K,
  raw: string | undefined,
  fallback: K extends keyof KnownPreferences ? KnownPreferences[K] : string,
): K extends keyof KnownPreferences ? KnownPreferences[K] : string {
  if (raw === undefined) return fallback;
  if (BOOLEAN_KEYS.includes(key as keyof KnownPreferences)) {
    return (raw === 'true') as never;
  }
  if (NULLABLE_KEYS.includes(key as keyof KnownPreferences) && raw === '') {
    return null as never;
  }
  return raw as never;
}

export function serialize(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}
